/**
 * Skills API client.
 *
 * Base URL: /api/orchestration/skills (Martha's own / Settings)
 * Env URL:  /api/archie/${env}/orchestration/skills (Archie platform per-environment)
 * All endpoints are system-wide (not project-scoped).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ArchieEnvironment } from './environments';

const BASE = '/api/orchestration/skills';

function envBase(env: ArchieEnvironment) {
  return `/api/archie/${env}/orchestration/skills`;
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = localStorage.getItem('archie_access_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { ...options, headers, credentials: 'include' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return res.json();
}

// ── Types ────────────────────────────────────────────────────────

export type SkillStatus = 'draft' | 'published' | 'archived';

export interface Skill {
  id: string;
  name: string;
  slug: string;
  description: string;
  content: string;
  category: string;
  isUserInvocable: boolean;
  version: number;
  status: SkillStatus;
  tags: string[];
  metadata: Record<string, unknown>;
  installCount: number;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SkillsListResponse {
  data: Skill[];
  pagination: { total: number; limit: number; offset: number };
}

export interface SkillResponse {
  data: Skill;
}

export interface CreateSkillPayload {
  name: string;
  slug: string;
  description?: string;
  content?: string;
  category?: string;
  isUserInvocable?: boolean;
  tags?: string[];
}

export interface UpdateSkillPayload {
  name?: string;
  description?: string;
  content?: string;
  category?: string;
  isUserInvocable?: boolean;
  status?: SkillStatus;
  tags?: string[];
}

// ── API functions ────────────────────────────────────────────────

export const skillsApi = {
  list: (params?: { status?: string; category?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.append('status', params.status);
    if (params?.category) qs.append('category', params.category);
    qs.append('limit', '100');
    return request<SkillsListResponse>(`${BASE}?${qs}`);
  },

  get: (id: string) => request<SkillResponse>(`${BASE}/${id}`),

  create: (payload: CreateSkillPayload) =>
    request<SkillResponse>(BASE, { method: 'POST', body: JSON.stringify(payload) }),

  update: (id: string, payload: UpdateSkillPayload) =>
    request<SkillResponse>(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),

  delete: (id: string) =>
    request<{ success: boolean }>(`${BASE}/${id}`, { method: 'DELETE' }),

  publish: (id: string) =>
    request<SkillResponse>(`${BASE}/${id}/publish`, { method: 'POST' }),
};

// ── Env-aware API (for Archie platform environments) ─────────────

export function envSkillsApi(env: ArchieEnvironment) {
  const base = envBase(env);
  return {
    list: (params?: { status?: string; category?: string }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.append('status', params.status);
      if (params?.category) qs.append('category', params.category);
      qs.append('limit', '100');
      return request<SkillsListResponse>(`${base}?${qs}`);
    },
    get: (id: string) => request<SkillResponse>(`${base}/${id}`),
    create: (payload: CreateSkillPayload) =>
      request<SkillResponse>(base, { method: 'POST', body: JSON.stringify(payload) }),
    update: (id: string, payload: UpdateSkillPayload) =>
      request<SkillResponse>(`${base}/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
    delete: (id: string) =>
      request<{ success: boolean }>(`${base}/${id}`, { method: 'DELETE' }),
    publish: (id: string) =>
      request<SkillResponse>(`${base}/${id}/publish`, { method: 'POST' }),
  };
}

// ── React Query hooks ────────────────────────────────────────────

export function useSkills(params?: { status?: string; category?: string }) {
  return useQuery({
    queryKey: ['skills', params],
    queryFn: () => skillsApi.list(params),
    staleTime: 30_000,
  });
}

export function useSkill(id: string | null) {
  return useQuery({
    queryKey: ['skill', id],
    queryFn: () => skillsApi.get(id!),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSkillPayload) => skillsApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['skills'] }),
  });
}

export function useUpdateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSkillPayload }) =>
      skillsApi.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['skills'] });
      qc.invalidateQueries({ queryKey: ['skill', id] });
    },
  });
}

export function useDeleteSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => skillsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['skills'] }),
  });
}

export function usePublishSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => skillsApi.publish(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['skills'] });
      qc.invalidateQueries({ queryKey: ['skill', id] });
    },
  });
}

// ── Env-aware React Query hooks ──────────────────────────────────

export function useEnvSkills(env: ArchieEnvironment, params?: { status?: string; category?: string }) {
  return useQuery({
    queryKey: ['env-skills', env, params],
    queryFn: () => envSkillsApi(env).list(params),
    staleTime: 30_000,
  });
}

export function useEnvSkill(env: ArchieEnvironment, id: string | null) {
  return useQuery({
    queryKey: ['env-skill', env, id],
    queryFn: () => envSkillsApi(env).get(id!),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useCreateEnvSkill(env: ArchieEnvironment) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSkillPayload) => envSkillsApi(env).create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['env-skills', env] }),
  });
}

export function useUpdateEnvSkill(env: ArchieEnvironment) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSkillPayload }) =>
      envSkillsApi(env).update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['env-skills', env] });
      qc.invalidateQueries({ queryKey: ['env-skill', env, id] });
    },
  });
}

export function useDeleteEnvSkill(env: ArchieEnvironment) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => envSkillsApi(env).delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['env-skills', env] }),
  });
}

export function usePublishEnvSkill(env: ArchieEnvironment) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => envSkillsApi(env).publish(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['env-skills', env] });
      qc.invalidateQueries({ queryKey: ['env-skill', env, id] });
    },
  });
}
